import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import moment from "jalali-moment";
import { Button, Spin } from "antd";
import {
  getExperimentParams,
  getExperimentsReportResults,
} from "@services/api";
import { hejriDate } from "@helpers/financial_helper";
import { FieldMarkerIcon } from "@/components/map/utils/customs";
import { soilSampleType } from "@/components/reports/soil/soilExperimentHelper";
import { toolbarBaseControls } from "@/features/map/utils/configs";
import AgroMapFarm from "@/features/map/presentation/components/AgroMapFarm";
import frame2182 from "@assets/images/leaf_map_report_images/header-page-leaf-result.png";
import frame2173 from "@assets/images/leaf_map_report_images/Vision statement-amico 3.png";
import line from "@assets/images/leaf_map_report_images/Line185.png";
import headerImage from "@assets/images/leaf_map_report_images/header-cover.png";
import footerImage from "@assets/images/leaf_map_report_images/footer-page.png";

function PlantResult() {
  const navigate = useNavigate();

  const { id } = useParams();

  const location = useLocation();
  const { returnTo, categoryId } = location.state;

  const soilExperimentReportQuery = useQuery({
    queryKey: ["soilExperimentReportQuery", id],
    queryFn: () => getExperimentsReportResults(id),
  });
  const experimentsParametersQuery = useQuery({
    queryKey: ["experimentsParametersQuery", categoryId],
    queryFn: () => getExperimentParams(),
    retry: 0,
  });

  let knownPoint = [];
  let phSample;
  let startOfStep;
  let sampleLength;
  let endOfStep;
  let rowCounter;
  let indexOfResult;
  let pageNumber = 0;
  let numberOfDynamicPages = [];
  let experimentParameterResults = [];
  let numberOf15;
  let maxLengthResult = 0;
  let maxSampleResultLength = [];

  if (soilExperimentReportQuery?.data?.experiment?.stations) {
    sampleLength =
      soilExperimentReportQuery?.data?.experiment?.stations[0]?.samples?.length;
  }

  soilExperimentReportQuery?.data?.experiment?.results?.forEach((station) => {
    if (station?.samples?.length > sampleLength) {
      sampleLength = station?.samples?.length;
    }
  });

  soilExperimentReportQuery?.data?.experiment?.results?.forEach((result) => {
    if (result?.sampleResults?.length > maxLengthResult) {
      maxSampleResultLength = result?.sampleResults.map((sampleResult) => ({
        ...sampleResult,
        parameter:
          experimentsParametersQuery?.data?.experimentsParameters?.find(
            (experimentParameter) =>
              experimentParameter.uuid === sampleResult.parameterId
          ),
      }));
      maxLengthResult = result?.sampleResults?.length;
    }
  });

  maxSampleResultLength = maxSampleResultLength?.filter(
    (sampleResult) => sampleResult?.result !== null
  );

  numberOf15 = maxSampleResultLength?.length / 18;

  while (numberOf15 > 0) {
    numberOfDynamicPages.push({ sample: "testObject" });
    --numberOf15;
  }

  soilExperimentReportQuery?.data?.experiment?.results?.forEach((result) => {
    if (
      soilExperimentReportQuery?.data?.experiment?.stations?.samples?.find(
        (sample) => result?.sampleCode === sample?.sampleCode
      )
    ) {
      phSample = result?.sampleResults?.find(
        (sampleResult) =>
          sampleResult?.parameterId === "f51668e8-81d2-4e48-acb5-6af6cdbaf089"
      );
      if (phSample) {
        knownPoint.push([
          soilExperimentReportQuery?.data?.experiment?.stations?.samples?.find(
            (sample) => sample?.sampleCode === result?.sampleCode
          )?.latitude,
          soilExperimentReportQuery?.data?.experiment?.stations?.samples?.find(
            (sample) => sample?.sampleCode === result?.sampleCode
          )?.longitude,
          phSample?.result,
        ]);
      }
    }
  });

  maxSampleResultLength?.sort(
    (prev, current) =>
      +prev?.parameter?.displayOrder - current?.parameter?.displayOrder
  );

  return (
    <>
      {soilExperimentReportQuery?.isLoading ? (
        <div className="flex flex-row items-center w-[500px] h-[500px] mx-auto my-[6rem]">
          <Spin
            className="spiner-loading-soil"
            style={{ margin: "auto" }}
            size="large"
          />
        </div>
      ) : (
        <div className="section-container flex flex-col items-center justify-center w-[720px] mx-auto !bg-white">
          {/* Page1 (Introduction) */}
          <div className="page1">
            <section className="flex flex-col justify-between items-center w-full h-[1048px]">
              <img
                className="header"
                src={headerImage}
                alt="header"
                width="720px"
              />

              <div className="flex flex-col justify-between items-center w-full mt-[-90px] leading-[4.25rem]">
                <p className="text-[22px] font-semibold text-center">
                  {" "}
                  شبكه آزمايشگاه‌هاي خوشه‌پروران ( اگرويار)
                </p>

                <p
                  className="text-[#222] text-[36px] font-bold !font-[B-Nazanin]"
                  style={{ textShadow: "0px 2px 4px rgba(0, 0, 0, 0.25)" }}
                >
                  کشاورزی{" "}
                  <span className="text-lab !font-[B-Nazanin]">
                    مطمئن‌
                    <span className="text-[#222] !font-[B-Nazanin]">تر، </span>
                  </span>
                  <span className="text-lab !font-[B-Nazanin]">
                    دقیق‌
                    <span className="text-[#222] !font-[B-Nazanin]">تر و </span>
                  </span>
                  <span className="text-lab !font-[B-Nazanin]">
                    اقتصادی‌
                    <span className="text-[#222] !font-[B-Nazanin]">تر</span>
                  </span>
                </p>

                <img
                  src={frame2173}
                  className="w-[420px] !font-[B-Nazanin]"
                  alt="chart-icon"
                />

                <span
                  className="text-lab font-bold text-[64px] !font-[B-Nazanin]"
                  style={{
                    textShadow: "1px 0px 3px rgba(118, 118, 118, 0.61)",
                  }}
                >
                  فرم نتایج آزمایش برگ
                </span>

                <p className="text-[#000] font-semibold text-[24px] !font-[B-Nazanin]">
                  هدف ما تصمیم‌گیری برای کشاورز نیست، کمک به تصمیم‌گیری بهتر
                  کشاورز است
                </p>

                <p className="text-lab font-bold text-[28px] tracking-[4px]">
                  WWW.AGROYAAR.COM
                </p>
              </div>

              <div className="footer w-full flex flex-col justify-start items-center relative">
                <img
                  className="absolute bottom-0 z-[2]"
                  src={footerImage}
                  width="720px"
                  alt="footer"
                />
                <span className="absolute bottom-[5px] left-[35px] tracking-[7px] z-[3] text-[14px] font-bold text-[#fff] !font-sans">
                  WWW.AGROYAAR.COM
                </span>
                <span className="absolute bottom-[5px] right-[15px] z-[3] text-[18px] font-semibold text-[#fff]">
                  {++pageNumber}
                </span>
              </div>
            </section>
          </div>

          {/* Page2 (Description) */}
          <div className="page2">
            <section className="flex flex-col justify-between items-center w-full h-[1048px]">
              <div className="header w-full relative">
                <img
                  className="header"
                  src={frame2182}
                  alt="header-image"
                  width="720px"
                />
                <span className="absolute top-1 left-[20px] z-[3] text-[14px] font-bold text-[#fff] tracking-[7px] text-center">
                  WWW.AGROYAAR.COM
                </span>
              </div>

              <div className="flex flex-row justify-start items-center w-full gap-5 mt-[-30px] px-4">
                <div className="flex flex-col justify-center items-start w-[50%] gap-2">
                  <span className="font-bold text-[20px] text-[#222] !font-[B-Nazanin]">
                    اطلاعات کشاورز
                  </span>

                  <div className="flex flex-row-reverse justify-end items-center gap-[19px] w-full">
                    <div className="text-agrotext font-bold text-[18px] flex flex-col justify-center items-start gap-[6px]">
                      <span className="!font-[B-Nazanin]">
                        {
                          soilExperimentReportQuery?.data?.experiment?.farmer
                            ?.firstName
                        }{" "}
                        {
                          soilExperimentReportQuery?.data?.experiment?.farmer
                            ?.lastName
                        }
                      </span>

                      <div className="flex flex-row items-start gap-2">
                        <span className="!font-[B-Nazanin]">کد کشاورزی:</span>
                        <span className="!font-sans text-[16px]">
                          {
                            soilExperimentReportQuery?.data?.experiment?.farmer
                              ?.farmerCode
                          }
                        </span>
                      </div>

                      <div className="flex flex-row items-start gap-2">
                        <span className="!font-[B-Nazanin]">استان و شهر:</span>
                        <span className="!font-[B-Nazanin]">
                          {
                            soilExperimentReportQuery?.data?.experiment?.farmer
                              ?.province
                          }
                          ،{" "}
                          {
                            soilExperimentReportQuery?.data?.experiment?.farmer
                              ?.city
                          }
                        </span>
                      </div>
                    </div>

                    <img
                      src={
                        process.env.REACT_APP_ARVAN_S3_BASE_URL +
                        soilExperimentReportQuery?.data?.experiment?.farmer
                          ?.avatar
                      }
                      alt="profile-image"
                      width="70px"
                      height="70px"
                      className="rounded-full border-[2px] border-solid border-lab"
                    />
                  </div>
                </div>

                {soilExperimentReportQuery?.data?.experiment?.expert && (
                  <div className="flex flex-col justify-center items-start w-[50%] gap-2">
                    <span className="font-bold text-[20px] text-[#222] !font-[B-Nazanin]">
                      اطلاعات کارشناس
                    </span>

                    <div className="flex flex-row-reverse justify-end items-center gap-[19px] w-full">
                      <div className="text-agrotext font-bold text-[18px] flex flex-col justify-center items-start gap-[6px]">
                        <span className="!font-[B-Nazanin]">
                          {
                            soilExperimentReportQuery?.data?.experiment?.expert
                              ?.firstName
                          }{" "}
                          {
                            soilExperimentReportQuery?.data?.experiment?.expert
                              ?.lastName
                          }
                        </span>

                        <div className="flex flex-row items-start gap-2">
                          <span className="!font-[B-Nazanin]">
                            کد کارشناسی:
                          </span>
                          <span className="!font-sans text-[16px]">
                            {
                              soilExperimentReportQuery?.data?.experiment
                                ?.expert?.expertCode
                            }
                          </span>
                        </div>

                        <div className="flex flex-row items-start gap-2">
                          <span className="!font-[B-Nazanin]">
                            استان و شهر:
                          </span>
                          <span className="!font-[B-Nazanin]">
                            {
                              soilExperimentReportQuery?.data?.experiment
                                ?.expert?.province
                            }
                            ،{" "}
                            {
                              soilExperimentReportQuery?.data?.experiment
                                ?.expert?.city
                            }
                          </span>
                        </div>
                      </div>

                      <img
                        src={
                          process.env.REACT_APP_ARVAN_S3_BASE_URL +
                          soilExperimentReportQuery?.data?.experiment?.expert
                            ?.avatar
                        }
                        alt="profile-image"
                        width="70px"
                        height="70px"
                        className="rounded-full border-[2px] border-solid border-lab"
                      />
                    </div>
                  </div>
                )}
              </div>

              <img
                src={line}
                alt="line"
                width="720px"
                className="mt-[-50px] mr-0 mb-[-30px] ml-0"
              />

              <div className="flex flex-col justify-start items-start w-full gap-5 px-4">
                <div className="flex flex-row items-center justify-center w-full gap-6">
                  <div className="flex flex-col justify-center items-start w-[50%] gap-2">
                    <span className="font-bold text-[20px] text-[#222] !font-[B-Nazanin]">
                      اطلاعات زمین
                    </span>
                    <div className="flex flex-row-reverse justify-end items-center gap-[19px] w-full">
                      <div className="text-agrotext font-bold text-[18px] flex flex-col justify-center items-start gap-[6px]">
                        <div className="flex flex-row items-start gap-2">
                          <span className="!font-[B-Nazanin]">نام قطعه:</span>
                          <span className="!font-[B-Nazanin]">
                            {
                              soilExperimentReportQuery?.data?.experiment
                                ?.season?.farm?.name
                            }
                          </span>
                        </div>
                        <div className="flex flex-row items-start gap-2">
                          <span className="!font-[B-Nazanin]">استان:</span>
                          <span className="!font-[B-Nazanin]">
                            {
                              soilExperimentReportQuery?.data?.experiment
                                ?.season?.farm?.province?.name
                            }
                          </span>
                        </div>
                        <div className="flex flex-row items-start gap-2">
                          <span className="!font-[B-Nazanin]">شهر:</span>
                          <span className="!font-[B-Nazanin]">
                            {
                              soilExperimentReportQuery?.data?.experiment
                                ?.season?.farm?.city?.name
                            }
                          </span>
                        </div>
                      </div>

                      <div className="relative w-[80px] h-[80px] overflow-clip rounded-[999px]">
                        <div className="THISISFORMAPLOCK! bg-transparent z-[99999999] w-full h-full absolute"></div>
                        <AgroMapFarm
                          mapId={
                            soilExperimentReportQuery?.data?.experiment?.season
                              ?.farm?.id
                          }
                          type="onlyShow"
                          toolbarOptions={toolbarBaseControls}
                          markerIcon={FieldMarkerIcon}
                          currentFarmPolygon={
                            soilExperimentReportQuery?.data?.experiment?.season
                              ?.farm?.polygon
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col justify-center items-start w-[50%] gap-2">
                    <span className="font-bold text-[20px] text-[#222] !font-[B-Nazanin]">
                      اطلاعات فصل رشدی
                    </span>

                    <div className="flex flex-row-reverse justify-end items-center gap-[19px] w-full">
                      <div className="text-agrotext font-bold text-[18px] flex flex-col justify-center items-start gap-[6px]">
                        <div className="flex flex-row items-start gap-2">
                          <span className="!font-[B-Nazanin]"> نام محصول:</span>
                          <span className="!font-[B-Nazanin]">
                            {
                              soilExperimentReportQuery?.data?.experiment
                                ?.season?.product?.persianName
                            }
                          </span>
                        </div>
                        <div className="flex flex-row items-start gap-2">
                          <span className="!font-[B-Nazanin]">مساحت زمین:</span>
                          <span className="!font-[B-Nazanin]">
                            {
                              soilExperimentReportQuery?.data?.experiment?.farm
                                ?.area
                            }{" "}
                            هکتار
                          </span>
                        </div>

                        <div className="flex flex-row items-start gap-2">
                          <span className="!font-[B-Nazanin]">فصل رشد:</span>
                          <span className="!font-[B-Nazanin]">
                            {moment(
                              `${soilExperimentReportQuery?.data?.experiment?.season.growingSeason}-04-01`,
                              "YYYY-MM-DD"
                            )
                              .locale("fa")
                              .format("YYYY")}{" "}
                            {soilExperimentReportQuery?.data?.experiment?.season
                              .persianName
                              ? ` - ${soilExperimentReportQuery?.data?.experiment?.season.persianName}`
                              : ""}
                          </span>
                        </div>
                      </div>
                      <div className="relative w-[80px] h-[80px] overflow-clip rounded-[999px]">
                        <div className="THISISFORMAPLOCK! bg-transparent z-[99999999] w-full h-full absolute"></div>
                        <AgroMapFarm
                          mapId={
                            soilExperimentReportQuery?.data?.experiment?.season
                              ?.id
                          }
                          type="onlyShow"
                          toolbarOptions={toolbarBaseControls}
                          markerIcon={FieldMarkerIcon}
                          currentFarmPolygon={
                            soilExperimentReportQuery?.data?.experiment?.season
                              ?.polygon
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <img
                src={line}
                alt="line"
                width="720px"
                className="mt-[-50px] mr-0 mb-[-30px] ml-0"
              />

              <div className="flex flex-col items-start justify-center w-full leading-[2rem] px-4 gap-2">
                <span className="font-bold text-[20px] text-[#222] !font-[B-Nazanin]">
                  اطلاعات آزمایش
                </span>

                <div className="flex flex-row items-center justify-start w-full gap-6">
                  <div className="flex flex-col text-agrotext font-bold text-[18px] justify-start items-start">
                    <div className="flex flex-row items-start gap-2">
                      <span className="!font-[B-Nazanin]">نوع نمونه:</span>
                      <span className="!font-[B-Nazanin]">
                        {soilSampleType(
                          soilExperimentReportQuery?.data?.experiment?.category
                            ?.categoryFA
                        )}
                      </span>
                    </div>

                    <div className="flex flex-row items-start gap-2">
                      <span className="!font-[B-Nazanin]">
                        تاریخ نمونه‌برداری:
                      </span>
                      <span className="!font-[B-Nazanin]">
                        {hejriDate(
                          soilExperimentReportQuery?.data?.experiment
                            ?.samplingAt
                        )}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-col text-agrotext font-bold text-[18px] justify-start items-start">
                    <div className="flex flex-row items-start gap-2">
                      <span className="!font-[B-Nazanin]">
                        منطقه گیاهی نمونه:
                      </span>
                      <span className="!font-[B-Nazanin]">
                        برگ‌های جوان توسعه‌یافته
                      </span>
                      {/* <span className='!font-[B-Nazanin]'>{samplingDepth(sampleLength)}</span> */}
                    </div>

                    <div className="flex flex-row items-start gap-2">
                      <span className="!font-[B-Nazanin]">
                        تاریخ دریافت نمونه:
                      </span>
                      <span className="!font-[B-Nazanin]">
                        {hejriDate(
                          soilExperimentReportQuery?.data?.experiment?.receiptAt
                        )}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-col text-agrotext font-bold text-[18px] justify-start items-start">
                    <div className="flex flex-row items-start gap-2">
                      <span className="!font-[B-Nazanin]">تعداد نمونه‌ها:</span>
                      <span className="!font-[B-Nazanin]">
                        {
                          soilExperimentReportQuery?.data?.experiment?.results
                            ?.length
                        }
                      </span>
                    </div>

                    <div className="flex flex-row items-start gap-2">
                      <span className="!font-[B-Nazanin]">
                        تاریخ آنالیز نمونه:
                      </span>
                      <span className="!font-[B-Nazanin]">
                        {hejriDate(
                          soilExperimentReportQuery?.data?.experiment
                            ?.analysisAt
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <img
                src={line}
                alt="line"
                width="750px"
                className="mt-[-50px] mr-0 mb-[-30px] ml-0"
              />

              <div className="flex flex-col justify-center items-start w-full gap-1 mb-[6.5rem] px-4">
                <span className="font-bold text-[20px] text-[#222] !font-[B-Nazanin]">
                  محل و نقاط نمونه برداری
                </span>

                <div className="w-[680px] h-[310px] rounded-lg bg-slate-200">
                  <div className="THISISFORMAPLOCK! bg-transparent z-[99999999] w-[680px] h-[310px] absolute"></div>

                  {/* <Map
                    service={Service.FieldView}
                    polygonCoordinates={
                      soilExperimentReportQuery?.data?.experiment?.farm?.polygon
                    }
                    index="17"
                    productId={
                      soilExperimentReportQuery?.data?.experiment?.farm
                        ?.productId
                    }
                    samplingStationPoints={
                      soilExperimentReportQuery?.data?.experiment?.stations
                    }
                    serviceReport={"SOIL"}
                  /> */}

                  <AgroMapFarm
                    mapId={`222${soilExperimentReportQuery?.data?.experiment?.season?.polygon}`}
                    type="onlyShow"
                    toolbarOptions={toolbarBaseControls}
                    markerIcon={FieldMarkerIcon}
                    currentFarmPolygon={
                      soilExperimentReportQuery?.data?.experiment?.season
                        ?.polygon
                    }
                    samplingStationPoints={
                      soilExperimentReportQuery?.data?.experiment?.stations
                    }
                    mapForWhatReport="EXPERIMENT"
                  />
                </div>
              </div>

              <div className="footer w-full flex flex-col justify-start items-center relative">
                <img
                  className="absolute bottom-0 z-[2]"
                  src={footerImage}
                  width="750px"
                  alt="footer-image"
                />
                <span className="absolute bottom-[5px] left-[35px] z-[3] text-[14px] font-bold text-[#fff] !font-sans">
                  کد آزمایش:{" "}
                  {soilExperimentReportQuery?.data?.experiment?.experimentCode}
                </span>
                <span className="absolute bottom-[5px] right-[15px] z-[3] text-[18px] font-semibold text-[#fff]">
                  {++pageNumber}
                </span>
              </div>
            </section>
          </div>

          {/* Page3 (generating dynamic pages) */}
          {soilExperimentReportQuery?.data?.experiment?.stations?.map(
            (station, index) => {
              startOfStep = 0;
              endOfStep = 0;
              indexOfResult = -1;
              experimentParameterResults = [];
              rowCounter = 0;
              return (
                <>
                  {numberOfDynamicPages.map((page, i) => {
                    startOfStep = endOfStep;
                    if (endOfStep + 18 > maxSampleResultLength?.length) {
                      endOfStep = maxSampleResultLength?.length;
                    } else {
                      endOfStep += 18;
                    }
                    return (
                      <div key={i} className="page3">
                        <section className="flex flex-col justify-between items-center w-full h-[1048px]">
                          <div className="header w-full relative">
                            <img
                              className="header"
                              src={frame2182}
                              alt="header-image"
                              width="720px"
                            />
                            <span className="absolute top-1 left-[20px] z-[3] text-[14px] font-bold text-[#fff] tracking-[7px] text-center">
                              WWW.AGROYAAR.COM
                            </span>
                          </div>

                          <div className="flex flex-col justify-start items-center h-full w-full gap-[10px]">
                            <div className="flex flex-row justify-between items-center w-full px-4">
                              <div className="flex flex-row items-start justify-center gap-2 text-right">
                                <span className="!font-[B-Nazanin] font-bold text-[16px] text-[#404848]">
                                  ایستگاه:
                                </span>
                                <span className="!font-[B-Nazanin] font-normal text-[16px] text-[#333]">
                                  {index + 1} از{" "}
                                  {
                                    soilExperimentReportQuery?.data?.experiment
                                      ?.stations?.length
                                  }
                                </span>
                              </div>

                              {station?.samples?.map((sample) => {
                                return (
                                  <div className="flex flex-row items-start justify-center gap-2 text-right">
                                    <span className="!font-[B-Nazanin] font-bold text-[15px] text-[#404848]">
                                      کد نمونه:
                                    </span>
                                    <span className="!font-[B-Nazanin] font-normal text-[16px] text-[#333]">
                                      {sample?.sampleCode}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>

                            <table
                              style={{
                                borderCollapse: "separate",
                                borderSpacing: 0,
                              }}
                              width={670}
                            >
                              <thead>
                                <tr>
                                  <td
                                    className="text-center"
                                    style={{
                                      borderTop: "2px solid #E4471A",
                                      borderRight: "2px solid #E4471A",
                                      borderBottom: "2px solid #E4471A",
                                      borderTopRightRadius: "10px",
                                      backgroundColor: "#FFEB9D",
                                    }}
                                  >
                                    <p className="!font-[B-Nazanin] text-[#333] text-[16px] pr-1 font-bold">
                                      ردیف
                                    </p>
                                  </td>
                                  <td
                                    className="text-center"
                                    style={{
                                      borderTop: "2px solid #E4471A",
                                      borderBottom: "2px solid #E4471A",
                                      backgroundColor: "#FFEB9D",
                                    }}
                                  >
                                    <p className="!font-[B-Nazanin] text-[#333] text-[16px] font-bold">
                                      علامت اختصاری
                                    </p>
                                  </td>
                                  <td
                                    className="text-center"
                                    style={{
                                      borderTop: "2px solid #E4471A",
                                      borderBottom: "2px solid #E4471A",
                                      backgroundColor: "#FFEB9D",
                                    }}
                                  >
                                    <p className="!font-[B-Nazanin] text-[#333] text-[16px] font-bold">
                                      نوع آزمایشات
                                    </p>
                                  </td>
                                  {station?.samples?.map((sample, i) => {
                                    return (
                                      <td
                                        key={i}
                                        className="text-center"
                                        style={{
                                          borderTop: "2px solid #E4471A",
                                          borderBottom: "2px solid #E4471A",
                                          backgroundColor: "#FFEB9D",
                                        }}
                                      >
                                        <p className="!font-[B-Nazanin] text-[#333] text-[14px] font-bold">
                                          نتیجه آزمایش
                                        </p>
                                      </td>
                                    );
                                  })}
                                  <td className="text-center border-t-[2px] border-b-[2px] border-l-[2px] border-solid border-[#E4471A] rounded-tl-[10px] bg-[#FFEB9D]">
                                    <p className="!font-[B-Nazanin] text-[#333] text-[16px] font-bold">
                                      واحد
                                    </p>
                                  </td>
                                </tr>
                              </thead>

                              <tbody>
                                {maxSampleResultLength
                                  ?.slice(startOfStep, endOfStep)
                                  ?.map((sampleResult, i) => {
                                    ++startOfStep;
                                    if (
                                      !experimentParameterResults.includes(
                                        sampleResult?.parameterId
                                      )
                                    ) {
                                      experimentParameterResults.push(
                                        sampleResult?.parameterId
                                      );
                                      ++indexOfResult;
                                    }
                                    return (
                                      <tr key={sampleResult?.id}>
                                        <td
                                          className="p-1"
                                          style={{
                                            borderTop:
                                              i + 1 !== 1 &&
                                              "1.5px solid #DCE7F9",
                                            borderRight: "2px solid #E4471A",
                                            borderBottom:
                                              startOfStep - 1 === endOfStep - 1
                                                ? "2px solid #E4471A"
                                                : "1.5px solid #DCE7F9",
                                            borderLeft: "1.5px solid #DCE7F9",
                                          }}
                                        >
                                          <p className="text-center !font-[B-Nazanin] text-[#333] text-[16px] font-bold">
                                            {++rowCounter}
                                          </p>
                                        </td>
                                        <td
                                          className="p-1"
                                          style={{
                                            borderTop:
                                              i + 1 !== 1 &&
                                              "1.5px solid #DCE7F9",
                                            borderBottom:
                                              startOfStep - 1 === endOfStep - 1
                                                ? "2px solid #E4471A"
                                                : "1.5px solid #DCE7F9",
                                            borderLeft: "1.5px solid #DCE7F9",
                                          }}
                                        >
                                          <p
                                            className={`text-center !font-[B-Nazanin] text-[#333] text-[16px] font-bold ${
                                              sampleResult?.parameter?.name &&
                                              "border-[2px] border-solid border-[#E4471A] rounded-[7px] bg-[#FFEB9D]"
                                            }`}
                                          >
                                            {sampleResult?.parameter?.name}
                                          </p>
                                        </td>
                                        <td
                                          className="p-1"
                                          style={{
                                            borderTop:
                                              i + 1 !== 1 &&
                                              "1.5px solid #DCE7F9",
                                            borderBottom:
                                              startOfStep - 1 === endOfStep - 1
                                                ? "2px solid #E4471A"
                                                : "1.5px solid #DCE7F9",
                                            borderLeft: "1.5px solid #DCE7F9",
                                          }}
                                        >
                                          <p className="text-center !font-[B-Nazanin] text-[#333] text-[16px] font-bold">
                                            {
                                              sampleResult?.parameter
                                                ?.persianName
                                            }
                                          </p>
                                        </td>
                                        {station?.samples?.map((sample) => {
                                          return (
                                            <td
                                              className="p-1"
                                              style={{
                                                borderTop:
                                                  i + 1 !== 1 &&
                                                  "1.5px solid #DCE7F9",
                                                borderBottom:
                                                  startOfStep - 1 ===
                                                  endOfStep - 1
                                                    ? "2px solid #E4471A"
                                                    : "1.5px solid #DCE7F9",
                                                borderLeft:
                                                  "1.5px solid #DCE7F9",
                                              }}
                                            >
                                              <p className="text-center !font-[B-Nazanin] text-[#333] text-[16px] font-bold">
                                                {
                                                  soilExperimentReportQuery?.data?.experiment?.results
                                                    ?.find(
                                                      (result) =>
                                                        result?.sampleCode ===
                                                        sample?.sampleCode
                                                    )
                                                    ?.sampleResults?.find(
                                                      (sampleResult) =>
                                                        experimentParameterResults[
                                                          indexOfResult
                                                        ] ===
                                                        sampleResult?.parameterId
                                                    )?.result
                                                }
                                              </p>
                                            </td>
                                          );
                                        })}
                                        <td
                                          className="p-1"
                                          style={{
                                            borderTop:
                                              i + 1 !== 1 &&
                                              "1.5px solid #DCE7F9",
                                            borderBottom:
                                              startOfStep - 1 === endOfStep - 1
                                                ? "2px solid #E4471A"
                                                : "1.5px solid #DCE7F9",
                                            borderLeft: "2px solid #E4471A",
                                          }}
                                        >
                                          <p className="text-center !font-[B-Nazanin] text-[#333] text-[16px] font-bold">
                                            {sampleResult?.parameter?.unit}
                                          </p>
                                        </td>
                                      </tr>
                                    );
                                  })}
                              </tbody>
                            </table>
                          </div>

                          <div className="footer w-full flex flex-col justify-start items-center relative">
                            <img
                              className="absolute bottom-0 z-[2]"
                              src={footerImage}
                              width="750px"
                              alt="footer-image"
                            />
                            {/* <img className="absolute bottom-2 left-[30px] z-[3]" src={agroyaarPlatform} alt="footer-image" /> */}
                            <span className="absolute bottom-[5px] left-[35px] z-[3] text-[14px] font-bold text-[#fff] !font-sans">
                              کد آزمایش:{" "}
                              {
                                soilExperimentReportQuery?.data?.experiment
                                  ?.experimentCode
                              }
                            </span>
                            <span className="absolute bottom-[5px] right-[15px] z-[3] text-[18px] font-semibold text-[#fff]">
                              {++pageNumber}
                            </span>
                          </div>
                        </section>
                      </div>
                    );
                  })}
                </>
              );
            }
          )}

          <div className="flex flex-col">
            <Button
              className="button-neo h-fit !text-[#333] hover:brightness-105 fixed top-[20px] right-[100px] max-lg:right-[32px] px-8 text-[18px]  w-[150px]"
              id="download-button"
              onClick={() => window.print()}
            >
              <p>پرینت گزارش</p>
            </Button>
            <Button
              className="button-neo h-fit !text-[#333] hover:brightness-105 fixed top-[65px] right-[100px] max-lg:right-[32px] px-8 text-[18px] w-[150px]"
              id="download-button"
              onClick={() => navigate(returnTo)}
            >
              <p> بازگشت</p>
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

export default PlantResult;
